// src/auth/AuthContext.js
import React, {
  createContext, useContext, useEffect, useMemo, useState, useCallback
} from "react";
import { auth, db } from "./firebaseClient";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  getIdToken
} from "firebase/auth";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  addDoc,
  onSnapshot,
  query,
  serverTimestamp,
  getDocs,
  where,
  getDoc
} from "firebase/firestore";
import { setTranslationOverrides } from "../i18n/translations";

const AuthContext = createContext(undefined);

// ----------------- helpers -----------------
const isHttpUrl = (s) => {
  try {
    const u = new URL(String(s).trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const rolesFor = (role) =>
    role === "admin" ? ["ROLE_ADMIN", "ROLE_MEMBER"] : ["ROLE_MEMBER"];

/**
 * Ensure /users/{uid} exists; return its data after ensuring.
 */
async function ensureUserDocument(db, uid, email, fallbackRole = "member") {
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(
        ref,
        {
          email: (email || "").toLowerCase(),
          role: fallbackRole,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        },
        { merge: true }
    );
  }
  const fresh = await getDoc(ref);
  return fresh.data() || { role: fallbackRole, email: (email || "").toLowerCase() };
}

// ----------------- provider -----------------
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null); // { uid, email, role, roles }
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Live collections
  const [teamMembers, setTeamMembers] = useState([]);
  const [books, setBooks] = useState([]);
  const [resources, setResources] = useState([]);
  const [siteContent, setSiteContent] = useState({});

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "siteContent"), (snapshot) => {
      const next = {};
      snapshot.forEach((doc) => {
        next[doc.id] = doc.data();
      });
      setSiteContent(next);
    }, (error) => {
      console.warn("Failed to load site content overrides", error);
      setSiteContent({});
    });

    return () => {
      unsub?.();
    };
  }, []);

  useEffect(() => {
    setTranslationOverrides(siteContent);
  }, [siteContent]);

  useEffect(() => {
    let userDocUnsub = null;
    let teamUnsub = null;
    let booksUnsub = null;
    let resourcesUnsub = null;

    const listenTeam = () =>
        onSnapshot(query(collection(db, "team"), orderBy("name")), (qs) => {
          setTeamMembers(qs.docs.map((d) => ({ id: d.id, ...d.data() })));
        });

    const listenBooks = () =>
        onSnapshot(query(collection(db, "books"), orderBy("title")), (qs) => {
          setBooks(qs.docs.map((d) => ({ id: d.id, ...d.data() })));
        });

    const listenResources = () =>
        onSnapshot(query(collection(db, "resources"), orderBy("title")), (qs) => {
          setResources(qs.docs.map((d) => ({ id: d.id, ...d.data() })));
        });

    const authUnsub = onAuthStateChanged(auth, async (fbUser) => {
      // clear previous listeners
      userDocUnsub?.();
      teamUnsub?.();
      booksUnsub?.();
      resourcesUnsub?.();

      if (!fbUser) {
        setCurrentUser(null);
        setToken(null);
        setTeamMembers([]);
        setBooks([]);
        setResources([]);
        // public listeners when logged out
        teamUnsub = listenTeam();
        booksUnsub = listenBooks();
        resourcesUnsub = listenResources();
        setLoading(false);
        return;
      }

      // Guarantee /users/{uid} exists (covers legacy users)
      await ensureUserDocument(db, fbUser.uid, fbUser.email, "member");

      // live user role from /users/{uid}
      const userRef = doc(db, "users", fbUser.uid);
      userDocUnsub = onSnapshot(userRef, (snap) => {
        const email = fbUser.email || "";
        const role = (snap.exists() && snap.data().role) || "member";
        setCurrentUser({
          uid: fbUser.uid,
          email,
          role,
          roles: rolesFor(role)
        });
      });

      // token (optional)
      try {
        const t = await getIdToken(fbUser, true);
        setToken(t);
      } catch {
        setToken(null);
      }

      // live collections (same when signed in, you may restrict reads in rules if needed)
      teamUnsub = listenTeam();
      booksUnsub = listenBooks();
      resourcesUnsub = listenResources();

      setLoading(false);
    });

    return () => {
      authUnsub?.();
      userDocUnsub?.();
      teamUnsub?.();
      booksUnsub?.();
      resourcesUnsub?.();
    };
  }, []);

  // ---------- AUTH ----------
  const login = useCallback(async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: "Unable to log in: " + (e?.message || "") };
    }
  }, []);

  const signup = useCallback(async ({ email, password }) => {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const normalizedEmail = (cred.user.email || "").toLowerCase();

      // Try to seed role from team table (if email appears with role admin)
      let role = "member";
      try {
        const teamSnap = await getDocs(
            query(collection(db, "team"), where("email", "==", normalizedEmail))
        );
        const adminDoc = teamSnap.docs.find(
            (d) => (d.data()?.role || "").toLowerCase() === "admin"
        );
        if (adminDoc) role = "admin";
      } catch (err) {
        console.warn("Failed to lookup team role during signup", err);
      }

      // Create /users/{uid} with initial role
      await setDoc(
          doc(db, "users", cred.user.uid),
          {
            email: normalizedEmail,
            role,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          },
          { merge: true }
      );

      return { ok: true };
    } catch (e) {
      return { ok: false, error: "Unable to create account: " + (e?.message || "") };
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  // ---------- TEAM ----------
  const createTeamMember = useCallback(async (payload) => {
    try {
      const target = doc(collection(db, "team"), payload.id);
      await setDoc(target, {
        id: payload.id,
        name: payload.name || "",
        email: (payload.email || "").toLowerCase(),
        role: payload.role || "member",
        prefix: typeof payload.prefix === "string" ? payload.prefix.trim() : "",
        bio: typeof payload.bio === "string" ? payload.bio.trim() : "",
        approved: !!payload.approved,
        verifiedBy: payload.verifiedBy || "",
        photoURL: isHttpUrl(payload.photoURL) ? payload.photoURL.trim() : "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "Unable to add member" };
    }
  }, []);

  const updateTeamMember = useCallback(async (docId, updates) => {
    try {
      const safe = { ...updates };
      if ("photoURL" in safe && safe.photoURL && !isHttpUrl(safe.photoURL)) safe.photoURL = "";
      if ("prefix" in safe && typeof safe.prefix === "string") safe.prefix = safe.prefix.trim();
      if ("bio" in safe && typeof safe.bio === "string") safe.bio = safe.bio.trim();
      await updateDoc(doc(db, "team", docId), {
        ...safe,
        updatedAt: serverTimestamp()
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "Unable to update member" };
    }
  }, []);

  const deleteTeamMember = useCallback(async (docId) => {
    try {
      await deleteDoc(doc(db, "team", docId));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "Unable to delete member" };
    }
  }, []);

  // ---------- BOOKS ----------
  const createBook = useCallback(
      async (payload) => {
        try {
          await addDoc(collection(db, "books"), {
            title: payload.title || "",
            author: payload.author || "",
            description: payload.description || "",
            coverURL: isHttpUrl(payload.coverURL) ? payload.coverURL.trim() : "",
            linkURL: isHttpUrl(payload.linkURL) ? payload.linkURL.trim() : "",
            tags: Array.isArray(payload.tags) ? payload.tags : [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: currentUser?.email || ""
          });
          return { ok: true };
        } catch (e) {
          return { ok: false, error: e?.message || "Unable to add book" };
        }
      },
      [currentUser]
  );

  const updateBook = useCallback(async (id, updates) => {
    try {
      const safe = { ...updates };
      if ("coverURL" in safe && safe.coverURL && !isHttpUrl(safe.coverURL)) safe.coverURL = "";
      if ("linkURL" in safe && safe.linkURL && !isHttpUrl(safe.linkURL)) safe.linkURL = "";
      await updateDoc(doc(db, "books", id), {
        ...safe,
        updatedAt: serverTimestamp()
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "Unable to update book" };
    }
  }, []);

  const deleteBook = useCallback(async (id) => {
    try {
      await deleteDoc(doc(db, "books", id));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "Unable to delete book" };
    }
  }, []);

  // ---------- RESOURCES ----------
  const createResource = useCallback(
      async (payload) => {
        try {
          await addDoc(collection(db, "resources"), {
            title: payload.title || "",
            description: payload.description || "",
            coverURL: isHttpUrl(payload.coverURL) ? payload.coverURL.trim() : "",
            linkURL: isHttpUrl(payload.linkURL) ? payload.linkURL.trim() : "",
            type: payload.type || "link",
            tags: Array.isArray(payload.tags) ? payload.tags : [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: currentUser?.email || ""
          });
          return { ok: true };
        } catch (e) {
          return { ok: false, error: e?.message || "Unable to add resource" };
        }
      },
      [currentUser]
  );

  const updateResource = useCallback(async (id, updates) => {
    try {
      const safe = { ...updates };
      if ("coverURL" in safe && safe.coverURL && !isHttpUrl(safe.coverURL)) safe.coverURL = "";
      if ("linkURL" in safe && safe.linkURL && !isHttpUrl(safe.linkURL)) safe.linkURL = "";
      await updateDoc(doc(db, "resources", id), {
        ...safe,
        updatedAt: serverTimestamp()
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "Unable to update resource" };
    }
  }, []);

  const deleteResource = useCallback(async (id) => {
    try {
      await deleteDoc(doc(db, "resources", id));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "Unable to delete resource" };
    }
  }, []);

  const saveSiteContent = useCallback(async (lang, overrides) => {
    const key = String(lang || "").trim();
    if (!key) {
      return { ok: false, error: "Language is required." };
    }
    if (overrides === null || typeof overrides !== "object" || Array.isArray(overrides)) {
      return { ok: false, error: "Overrides must be an object." };
    }
    try {
      await setDoc(doc(db, "siteContent", key), overrides, { merge: false });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "Unable to save site text." };
    }
  }, []);

  const deleteSiteContent = useCallback(async (lang) => {
    const key = String(lang || "").trim();
    if (!key) {
      return { ok: false, error: "Language is required." };
    }
    try {
      await deleteDoc(doc(db, "siteContent", key));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "Unable to remove site text overrides." };
    }
  }, []);

  /**
   * Auto-promote to admin if:
   *  - current user email appears in team with role 'admin'
   *  - current /users/{uid}.role !== 'admin'
   * Requires rules that allow users to update their own /users doc.
   */
  useEffect(() => {
    if (!currentUser?.uid || !currentUser.email) return;
    const normalizedEmail = currentUser.email.toLowerCase();
    const adminMember = teamMembers.find(
        (m) =>
            (m.email || "").toLowerCase() === normalizedEmail &&
            (m.role || "").toLowerCase() === "admin"
    );
    if (!adminMember || currentUser.role === "admin") return;

    updateDoc(doc(db, "users", currentUser.uid), {
      role: "admin",
      updatedAt: serverTimestamp()
    }).catch((err) => {
      console.warn("Failed to promote user to admin", err);
    });
  }, [teamMembers, currentUser]);

  const value = useMemo(
      () => ({
        token,
        currentUser,
        loading,
        teamMembers,
        books,
        resources,
        siteContent,
        login,
        signup,
        logout,
        createTeamMember,
        updateTeamMember,
        deleteTeamMember,
        createBook,
        updateBook,
        deleteBook,
        createResource,
        updateResource,
        deleteResource,
        saveSiteContent,
        deleteSiteContent
      }),
      [
        token,
        currentUser,
        loading,
        teamMembers,
        books,
        resources,
        siteContent,
        login,
        signup,
        logout,
        createTeamMember,
        updateTeamMember,
        deleteTeamMember,
        createBook,
        updateBook,
        deleteBook,
        createResource,
        updateResource,
        deleteResource,
        saveSiteContent,
        deleteSiteContent
      ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
